import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Footer } from "./Footer";

const SECTIONS = [
  {
    title: "1. Who we are",
    body:
      "MediElite Practice Solutions (\"MediElite\", \"we\", \"us\", or \"our\") is a US healthcare consulting firm offering Revenue Cycle Management, provider credentialing, provider licensing, and bookkeeping services. Our principal place of business is 971 US Highway 202 N, Branchburg, NJ 08876. You can reach us at info@medielitesolutions.com or +1 786-891-5066.",
  },
  {
    title: "2. Scope of this policy",
    body:
      "This Privacy Policy explains how we collect, use, disclose, and safeguard information collected through our website medielitesolutions.com and any communications initiated through our consultation forms, emails, or phone. This policy applies to prospective clients, website visitors, and business contacts. It does not cover protected health information (PHI) processed under a Business Associate Agreement (BAA) with an existing client; that data is governed separately by HIPAA and the applicable BAA.",
  },
  {
    title: "3. Information we collect",
    body:
      "When you submit a consultation request or contact us, we collect the details you voluntarily provide: your name, email address, phone number, practice or company name, services you are interested in, and any message you send us. Our website may also automatically log basic technical data such as IP address, browser type, pages visited, and referring URL for security, analytics, and performance monitoring.",
  },
  {
    title: "4. How we use your information",
    body:
      "We use your information only for legitimate business purposes: to respond to your inquiry, schedule and deliver consultations, provide the services you request, communicate service updates, keep our records, meet legal or regulatory obligations, and improve our website and offerings. We do not sell your personal information. We do not share your information for third-party marketing.",
  },
  {
    title: "5. Confidentiality of visitor details",
    body:
      "The contact details and messages you share via our forms are treated as confidential. Access is limited to the MediElite team members who need it to respond to your request. We do not disclose, rent, or sell visitor details to any third party. If we ever need to work with a vendor to process communications (for example, an email delivery provider), we contractually require them to protect your information and use it only for the specific service they perform for us.",
  },
  {
    title: "6. Data security",
    body:
      "We implement administrative, technical, and physical safeguards designed to protect your information — including access controls, encryption in transit (HTTPS/TLS), audit logging, and least-privilege access to production systems. No method of internet transmission or electronic storage is 100% secure, so while we strive to protect your information, we cannot guarantee absolute security.",
  },
  {
    title: "7. Data retention",
    body:
      "We retain consultation submissions and correspondence for as long as necessary to fulfill the purposes described in this policy, comply with our legal obligations, resolve disputes, and enforce our agreements. Records that are no longer needed are deleted or anonymized on a reasonable schedule.",
  },
  {
    title: "8. Your rights",
    body:
      "Depending on your state of residence, you may have the right to request access to, correction of, or deletion of the personal information we hold about you, and to opt out of certain uses. To exercise these rights, email us at info@medielitesolutions.com from the address you used to contact us. We will respond within the timeframe required by applicable law.",
  },
  {
    title: "9. Cookies and analytics",
    body:
      "Our website may use essential cookies for basic functionality and, where enabled, privacy-friendly analytics to understand aggregate usage. We do not use cookies to build cross-site advertising profiles of visitors.",
  },
  {
    title: "10. Third-party links",
    body:
      "Our website may reference third-party platforms (such as insurance payers, EHR vendors, or software partners) for informational purposes. We are not responsible for the privacy practices of those third parties. Please review their policies before providing information to them.",
  },
  {
    title: "11. Children",
    body:
      "Our services are directed at healthcare professionals and businesses. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us so we can remove it.",
  },
  {
    title: "12. Changes to this policy",
    body:
      "We may update this Privacy Policy from time to time. When we do, we will change the \"Last updated\" date at the top of this page. Material changes will be highlighted on this page for a reasonable period.",
  },
  {
    title: "13. Contact us",
    body:
      "If you have any questions or concerns about this Privacy Policy or our privacy practices, contact us at info@medielitesolutions.com, +1 786-891-5066, or by mail at 971 US Highway 202 N, Branchburg, NJ 08876.",
  },
];

const LAST_UPDATED = "December 2025";

export const PrivacyPolicy = () => (
  <div data-testid="privacy-page" className="min-h-screen bg-white">
    <div className="border-b border-slate-200 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-5 md:px-8 py-6 flex items-center justify-between">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-[color:var(--brand-green-700)]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to MediElite home
        </Link>
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/assets/logo.png"
            alt="MediElite Practice Solutions"
            className="h-10 md:h-12 w-auto"
          />
        </Link>
      </div>
    </div>

    <section className="py-12 md:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-5 md:px-8">
        <span className="inline-flex items-center gap-2 rounded-full bg-brand-green-100 text-[color:var(--brand-green-700)] px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase">
          <ShieldCheck className="w-4 h-4" /> Privacy Policy
        </span>
        <h1
          data-testid="privacy-title"
          className="font-display mt-5 text-4xl md:text-5xl font-bold text-navy leading-tight"
        >
          Your details stay confidential.
        </h1>
        <p className="mt-3 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>

        <div className="mt-8 rounded-2xl bg-[color:var(--brand-green-50)] border border-[color:var(--brand-green-200)] p-5 md:p-6 text-[15px] text-slate-700 leading-relaxed">
          <strong className="text-navy">In plain English:</strong> we treat the details you share
          on this website as confidential. We do not sell them, we do not share them with
          third-party marketers, and we only use them to respond to your request or deliver the
          service you asked for.
        </div>

        <div className="mt-10 space-y-8">
          {SECTIONS.map((s, i) => (
            <article key={s.title} data-testid={`privacy-section-${i}`}>
              <h2 className="font-display text-xl md:text-2xl font-semibold text-navy">
                {s.title}
              </h2>
              <p className="mt-2 text-[15px] md:text-base text-slate-700 leading-relaxed">
                {s.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default PrivacyPolicy;
